import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { unstable_batchedUpdates } from 'react-dom';
import {
  setSelectedLinkId,
  setSelectedVertex,
  exportGraph,
  exportNetworkMap,
  setSelectedSuggestedEdge,
} from 'store/graph/actions';
import PersonCard from 'common/node-cards/person-card';
import OrganizationCard from 'common/node-cards/organization';
import FundCard from 'common/node-cards/fund';
import EducationCard from 'common/node-cards/education';
import EngagementCard from 'common/node-cards/engagement';
import EventCard from 'common/node-cards/event-card';
import { AiOutlineExport, AiOutlineCamera } from 'react-icons/ai';
import { MdArrowDropDown, MdUndo } from 'react-icons/md';
import {
  selectContextIds,
  selectContextsObject,
  selectNodes,
  selectSelectedEdgeId,
  selectFocusedNode,
  selectFocusPingTick,
  selectSuggestedEdges,
} from 'store/graph/selectors';
import { nodeType } from 'utils/constants';
import {
  GetNodeSelectionStyles,
  isLink,
  IsObjectEmpty,
} from 'utils/graph-helper';
import {
  setClarityContext,
  clearClarityContext,
  setClarityContextIds,
} from 'utils/clarity-context';
import {
  selectShortestPathNodeId,
  selectShowVisibleConnections,
  selectAffiliationClusterMode,
  selectAffiliationFilter,
} from 'store/filters/selectors';
import { setShortestPathNodeId } from 'store/filters/actions';
import { itemsToCytoscapeElements, createTransformCache } from 'utils/cytoscape-transform';
import { baseStylesheet } from './cytoscape-stylesheet';
import './loading.css';
import GraphAdapter, { LAYOUT_PRESETS } from './graph-adapter';
import LayoutSwitcher from './layout-switcher';
import SuggestedPersonAnnotation from './suggested-person-annotation';
import FilterSessionAnnotation from './filter-session-annotation';
import styles from './graph.module.css';
import { perfMark, perfMeasure, perfCount } from 'utils/graph-perf';

// ── Extracted constant styles (avoid re-creating each render) ─────────
const CONTAINER_STYLE = { width: '100%', height: '100%', position: 'relative', zIndex: 0 };
const LAYOUT_OVERLAY_STYLE = {
  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '20px',
  background: 'rgba(24, 28, 36, 0.72)', backdropFilter: 'blur(4px)', zIndex: 4,
  transition: 'opacity 0.25s ease-out',
};
const LAYOUT_SPINNER_STYLE = { transform: 'scale(0.7)' };
const LAYOUT_TEXT_STYLE = { fontSize: '15px', fontWeight: 600, color: '#ffffffdd', letterSpacing: '0.6px' };
// Show the overlay only when the layout is genuinely heavy. Below this threshold
// the perceived latency tax (≥ 600 ms) dominates the actual layout cost.
const OVERLAY_NODE_THRESHOLD = 250;
const OVERLAY_ADDED_THRESHOLD = 25;
const shouldShowOverlay = (nodeCount, addedCount) =>
  nodeCount > OVERLAY_NODE_THRESHOLD && addedCount > OVERLAY_ADDED_THRESHOLD;
const NO_PATH_WRAPPER_STYLE = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, 0)',
  background: '#fff',
  border: '1px solid black',
  padding: '20px 10px',
  borderRadius: '4px',
};
const NO_PATH_TEXT_STYLE = { margin: '0', paddingBottom: '8px' };
const GO_BACK_BUTTON_STYLE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 10px',
  background: 'var(--skyBlueLight)',
  border: ' 1px solid var(--skyBlue)',
  borderRadius: '4px',
  outline: 'none',
  cursor: 'pointer',
  fontWeight: 700,
  color: '#fff',
  textTransform: 'uppercase',
  fontSize: '12px',
  marginTop: '8px',
};

function isNode(item) {
  return item && item.id1 == null && item.id2 == null;
}

/**
 * Build adjacency map from a flat items dict.
 * Memoized once per `items` reference via useMemo at the call site (S14).
 */
function buildAdjacency(items) {
  perfMark('adjacency:start');
  perfCount('adjacency.builds');
  const adj = new Map();
  if (!items) {
    perfMeasure('adjacency', 'adjacency:start');
    return adj;
  }
  for (const key in items) {
    const item = items[key];
    if (item?.id1 != null && item?.id2 != null) {
      if (!adj.has(item.id1)) adj.set(item.id1, []);
      if (!adj.has(item.id2)) adj.set(item.id2, []);
      adj.get(item.id1).push({ edgeId: key, neighborId: item.id2 });
      adj.get(item.id2).push({ edgeId: key, neighborId: item.id1 });
    }
  }
  perfMeasure('adjacency', 'adjacency:start');
  return adj;
}

/**
 * Multi-target BFS from a single source. Returns a dict of items along
 * shortest paths to ANY of the requested target ids that are reachable.
 * Replaces N independent BFS runs (one per target) with one (S14, H21).
 */
function bfsPathsFromSource(adj, items, sourceId, targets) {
  perfMark('findShortestPath:start');
  perfCount('findShortestPath.calls');
  const result = {};
  if (!items || !items[sourceId]) {
    perfMeasure('findShortestPath', 'findShortestPath:start');
    return result;
  }
  const wantedTargets = new Set();
  for (const t of targets) {
    if (t === sourceId) continue;
    if (items[t]) wantedTargets.add(t);
  }
  if (wantedTargets.size === 0) {
    perfMeasure('findShortestPath', 'findShortestPath:start');
    return result;
  }
  const visited = new Set([sourceId]);
  const parent = new Map();
  const queue = [sourceId];
  let remaining = wantedTargets.size;

  while (queue.length > 0 && remaining > 0) {
    const current = queue.shift();
    if (wantedTargets.has(current)) {
      let node = current;
      while (node !== sourceId) {
        result[node] = items[node];
        const { fromNode, viaEdge } = parent.get(node);
        result[viaEdge] = items[viaEdge];
        node = fromNode;
      }
      result[sourceId] = items[sourceId];
      remaining--;
    }
    const neighbors = adj.get(current);
    if (!neighbors) continue;
    for (let i = 0; i < neighbors.length; i++) {
      const { edgeId, neighborId } = neighbors[i];
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        parent.set(neighborId, { fromNode: current, viaEdge: edgeId });
        queue.push(neighborId);
      }
    }
  }
  perfMeasure('findShortestPath', 'findShortestPath:start');
  return result;
}

/**
 * BFS reachability seeded from all context ids using the shared adjacency.
 * Returns nodes + connecting edges reachable from any context.
 */
function findReachableFromAdjacency(adj, items, contextIds) {
  perfMark('findReachable:start');
  perfCount('findReachable.calls');
  if (!items || !contextIds) {
    perfMeasure('findReachable', 'findReachable:start');
    return {};
  }
  const visited = new Set();
  const queue = [];
  const result = {};

  for (const cid of Object.keys(contextIds)) {
    if (items[cid] && !visited.has(cid)) {
      visited.add(cid);
      queue.push(cid);
    }
  }

  while (queue.length > 0) {
    const current = queue.shift();
    result[current] = items[current];
    const neighbors = adj.get(current);
    if (!neighbors) continue;
    for (let i = 0; i < neighbors.length; i++) {
      const { edgeId, neighborId } = neighbors[i];
      if (items[edgeId]) result[edgeId] = items[edgeId];
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        queue.push(neighborId);
      }
    }
  }

  perfMeasure('findReachable', 'findReachable:start');
  return result;
}

/**
 * CytoscapeChartV2 — Performance-optimized Cytoscape chart component.
 *
 * Improvements over v1:
 * - Element diffing (only adds/removes/updates changed elements)
 * - Conditional layout (only re-runs when topology changes)
 * - RAF-throttled viewport events (eliminates 60+/sec re-renders during zoom/pan)
 * - BFS-based visible connections (independent of Cytoscape graph state)
 * - Data-driven edge colors via stylesheet (replaces 8 substring selectors)
 * - Extracted constant styles (no inline object creation per render)
 * - useCallback for export handlers
 */
const CytoscapeChart = React.forwardRef(({ items, isCollapsed }, ref) => {
  const dispatch = useDispatch();
  const edgeId = useSelector(selectSelectedEdgeId);
  const contextids = useSelector(selectContextIds);
  const contexts = useSelector(selectContextsObject);
  const showVisible = useSelector(selectShowVisibleConnections);
  const shortestPathNodeId = useSelector(selectShortestPathNodeId);
  const nodesDict = useSelector(selectNodes);
  const suggestedEdges = useSelector(selectSuggestedEdges);
  const suggestedEdgesRef = useRef(suggestedEdges);
  suggestedEdgesRef.current = suggestedEdges;
  // focusedNode is now a string id (or null), not an object.
  const focusedNodeId = useSelector(selectFocusedNode);
  // F-05 — increments on every setFocusedNode dispatch including
  // same-id repeats. Used as a useEffect dep to re-fire fit + ping
  // without invalidating chart memos.
  const focusPingTick = useSelector(selectFocusPingTick);
  // Derive the target node for radial layout: prefer focusedNode, fall back to first contextId
  const contextIdKeys = useMemo(() => Object.keys(contextids || {}), [contextids]);
  const targetNodeId = focusedNodeId || contextIdKeys[contextIdKeys.length - 1] || null;
  const affiliationClusterMode = useSelector(selectAffiliationClusterMode);
  const affiliationFilter = useSelector(selectAffiliationFilter);

  const [visibleConnections, setVisibleConnections] = useState(null);
  const [paths, setPathsConnections] = useState(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [activeLayout, setActiveLayout] = useState('fcose');
  const activeLayoutRef = useRef('fcose');
  const userExplicitLayoutRef = useRef(false);
  const [annotationPosition, setAnnotationPosition] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [isLayoutRunning, setIsLayoutRunning] = useState(false);

  const containerRef = useRef(null);
  const adapterRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const itemsRef = useRef(items);
  const nodesDictRef = useRef(nodesDict);
  const rafRef = useRef(null);
  // Instance-scoped transform cache (B1/B2). Cleared on unmount so multiple
  // chart mounts (StrictMode, route changes) never share or leak entries.
  const transformCacheRef = useRef(null);
  if (transformCacheRef.current == null) {
    transformCacheRef.current = createTransformCache();
  }
  itemsRef.current = items;
  nodesDictRef.current = nodesDict;

  // ── Expose adapter methods via ref ──────────────────────────────────
  useImperativeHandle(ref, () => ({
    fit: (opts) => adapterRef.current?.fit(opts?.ids),
    ping: (opts) => adapterRef.current?.ping(opts?.ids?.[0], opts?.duration),
    export: () => adapterRef.current?.exportPng(),
    getPositions: () => adapterRef.current?.getPositions(),
    getViewCoordinatesOfItem: (id) => adapterRef.current?.renderedPosition(id),
    viewCoordinates: (x, y) => adapterRef.current?.modelToRenderedPosition({ x, y }),
    getAdapter: () => adapterRef.current,
  }));

  // ── Determine which items to render ─────────────────────────────────
  const chartItems = useMemo(() => {
    const baseItems = showVisible
      ? visibleConnections
      : shortestPathNodeId
      ? paths
      : items;

    if (
      focusedNodeId &&
      baseItems &&
      baseItems[focusedNodeId] &&
      isNode(baseItems[focusedNodeId])
    ) {
      const fullNode = nodesDict[focusedNodeId]?.node;
      if (fullNode) {
        const selectionStyles = GetNodeSelectionStyles(
          fullNode,
          affiliationClusterMode ? affiliationFilter : null,
        );
        return {
          ...baseItems,
          [focusedNodeId]: {
            ...baseItems[focusedNodeId],
            ...selectionStyles,
            size: 12,
          },
        };
      }
    }
    return baseItems;
  }, [showVisible, visibleConnections, shortestPathNodeId, paths, items, focusedNodeId, nodesDict, affiliationClusterMode, affiliationFilter]);

  // ── Convert items → Cytoscape elements (with delta, Phase 7) ────────
  const transformResult = useMemo(
    () => itemsToCytoscapeElements(chartItems, {
      affiliationClusterMode,
      affiliationFilter,
      cache: transformCacheRef.current,
      withDelta: true,
    }),
    [chartItems, affiliationClusterMode, affiliationFilter],
  );
  const elements = transformResult.elements;

  // ── Shared adjacency map (S14) — one build per items change, reused by
  // both visible-connections and shortest-path effects below. Replaces the
  // per-context adjacency rebuild and the per-context BFS.
  const adjacency = useMemo(() => buildAdjacency(items), [items]);

  // ── Initialise Cytoscape ────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const adapter = new GraphAdapter();
    adapter.init(containerRef.current, {
      stylesheet: baseStylesheet,
      elements,
    });
    adapterRef.current = adapter;

    const cy = adapter.getCy();

    // ── Selection handler ─────────────────────────────────────────────
    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        unstable_batchedUpdates(() => {
          dispatch(setSelectedLinkId(null));
          // CRITICAL: Do NOT clear selectedSuggestedEdge here (race condition).
          setSelectedItemId(null);
          setAnnotationPosition(null);
        });
        cy.batch(() => {
          cy.elements().removeClass('dimmed');
          cy.elements().removeData('_dim');
        });
        return;
      }

      const target = evt.target;
      const id = target.id();
      const itemData = target.data();

      unstable_batchedUpdates(() => {
        if (target.isEdge()) {
          const isSuggested = Boolean(suggestedEdgesRef.current?.[id] || itemData.isSuggested);
          if (isSuggested) {
            dispatch(setSelectedSuggestedEdge(id));
            dispatch(setSelectedLinkId(null));
          } else {
            dispatch(setSelectedLinkId(id));
            dispatch(setSelectedSuggestedEdge(null));
          }
          setSelectedItemId(id);
          setAnnotationPosition(null);
        } else if (target.isNode()) {
          if (adapter.isComboToggleable(id)) {
            if (adapter.isComboExpanded(id)) {
              adapter.collapseCombo(id);
            } else {
              adapter.expandCombo(id);
            }
            return;
          }
          dispatch(setSelectedSuggestedEdge(null));
          setSelectedItemId(id);
          const pos = target.renderedPosition();
          setAnnotationPosition({ x: pos.x, y: pos.y });
        }
      });
    });

    // ── Hover handlers (RAF-throttled — S8) ────────────────────
    // Above this element count, the edge hover label (text-rotation/autorotate
    // + text-background) is disabled; rotating per-frame text on bezier edges
    // is the dominant cost during hover sweeps. Threshold raised from 300 to
    // 500 per product call (F-04 in the regression audit).
    const HOVER_LABEL_MAX_ELEMENTS = 500;
    let hoverRaf = null;
    let pendingHoverIn = null;
    let pendingHoverOut = null;
    const flushHover = () => {
      hoverRaf = null;
      const elementCount = cy.elements().length;
      const showEdgeLabel = elementCount <= HOVER_LABEL_MAX_ELEMENTS;
      if (pendingHoverOut) {
        pendingHoverOut.removeClass('hovered');
        pendingHoverOut.removeClass('highlighted');
        if (pendingHoverOut.isEdge && pendingHoverOut.isEdge()) {
          pendingHoverOut.removeClass('hovered-edge-label');
        }
        pendingHoverOut = null;
      }
      if (pendingHoverIn) {
        const target = pendingHoverIn;
        if (target.isNode && target.isNode() && !target.isParent()) {
          target.addClass('hovered');
          document.body.style.cursor = 'pointer';
        } else if (target.isEdge && target.isEdge()) {
          const id = target.id();
          if (suggestedEdgesRef.current?.[id]) target.addClass('highlighted');
          target.addClass('hovered');
          if (showEdgeLabel) target.addClass('hovered-edge-label');
          document.body.style.cursor = 'pointer';
        }
        pendingHoverIn = null;
      }
    };
    const scheduleHover = () => {
      if (hoverRaf != null) return;
      hoverRaf = requestAnimationFrame(flushHover);
    };
    cy.on('mouseover', 'node, edge', (evt) => {
      perfCount(evt.target.isNode() ? 'cy.hover.node.enter' : 'cy.hover.edge.enter');
      perfMark('cy:hover');
      pendingHoverIn = evt.target;
      scheduleHover();
    });
    cy.on('mouseout', 'node, edge', (evt) => {
      pendingHoverOut = evt.target;
      // If the same element still has a pending "in" event, drop it — the
      // user has already moved off.
      if (pendingHoverIn && pendingHoverIn.id && evt.target.id && pendingHoverIn.id() === evt.target.id()) {
        pendingHoverIn = null;
      }
      document.body.style.cursor = 'default';
      scheduleHover();
    });

    // ── Neighbor dimming on select (data-attribute, S2) ────────
    // Approach: set `data._dim = true` on the inverse collection in one
    // batched write. A stylesheet rule (`node[?_dim], edge[?_dim]`) applies
    // opacity 0.25. Replaces the previous `addClass('dimmed')` per-element
    // style invalidation pattern.
    cy.on('select', 'node', (evt) => {
      perfMark('cy:dim:start');
      perfCount('cy.dim.select');
      const selected = evt.target;
      if (selected.isParent()) {
        perfMeasure('cy:dim', 'cy:dim:start');
        return;
      }
      const keep = selected.closedNeighborhood();
      cy.batch(() => {
        cy.elements().difference(keep).data('_dim', true);
        keep.removeData('_dim');
      });
      perfMeasure('cy:dim', 'cy:dim:start');
    });
    cy.on('unselect', 'node', () => {
      perfMark('cy:dim:start');
      perfCount('cy.dim.unselect');
      // Defensive: when the user clicks a different node, Cytoscape may fire
      // `unselect` for the old node AFTER `select` for the new node. Don't
      // wipe the dim flags if there's still something selected — the new
      // selection's own `select` handler already set the flags we want.
      if (cy.$('node:selected').length === 0) {
        cy.batch(() => {
          cy.elements().removeData('_dim');
          cy.elements().removeClass('dimmed');
        });
      }
      perfMeasure('cy:dim', 'cy:dim:start');
    });

    // ── Viewport changes → RAF-throttled annotation repositioning ─────
    cy.on('viewport', () => {
      if (rafRef.current) return; // already scheduled
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setZoom(cy.zoom());
      });
    });

    // ── Drag handler ──────────────────────────────────────────────────
    cy.on('free', 'node', (evt) => {
      const id = evt.target.id();
      if (id === selectedItemId) {
        const pos = evt.target.renderedPosition();
        setAnnotationPosition({ x: pos.x, y: pos.y });
      }
    });

    // Fit to all on first render — use force-directed (fcose) as default
    adapter.runLayout(LAYOUT_PRESETS.fcose.options);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (hoverRaf != null) {
        cancelAnimationFrame(hoverRaf);
        hoverRaf = null;
      }
      pendingHoverIn = null;
      pendingHoverOut = null;
      document.body.style.cursor = 'default';
      // F-22 — DO NOT touch `transformCacheRef.current` here. Earlier this
      // cleanup partially cleared the cache (`itemRefs.clear()` +
      // `elements.clear()`) which left the idempotency fields
      // (`_lastInputItems`, `_lastResult`) stale. Under React StrictMode
      // dev, the cleanup-then-remount cycle would emit a `removedIds: []`
      // delta on subsequent context removals because the main path walked
      // the empty `_prevElements`, even though cy still had nodes to
      // remove. The cache is per-instance via useRef and is naturally
      // garbage-collected when the component unmounts; explicit clearing
      // here is unnecessary AND was actively causing the bug.
      adapter.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Update elements when data changes (DIFFING + CONDITIONAL LAYOUT) ─
  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;
    // Pass the dirty-id delta to setElements so it walks only changed ids.
    const result = adapter.setElements(transformResult);
    if (!result.topologyChanged) return;

    const isPureRemoval = result.addedCount === 0 && result.removedCount > 0;
    const isAdd = result.addedCount > 0;
    if (!isAdd && !isPureRemoval) return;

    const cy = adapter.getCy();
    const nodeCount = cy ? cy.nodes().length : 0;
    const showOverlay = shouldShowOverlay(nodeCount, result.addedCount);

    let cancelled = false;
    if (showOverlay) setIsLayoutRunning(true);

    const currentLayout = activeLayoutRef.current;
    const preset = LAYOUT_PRESETS[currentLayout] || LAYOUT_PRESETS.fcose;
    // S6 / Phase 11 — only flag as incremental for genuinely small deltas
    // (e.g. adding one context). Large filter-driven changes need fit so the
    // viewport reflects the new node set.
    const SMALL_DELTA = 25;
    const isSmallDelta =
      result.addedCount <= SMALL_DELTA && result.removedCount <= SMALL_DELTA;
    const layoutMeta = {
      addedCount: result.addedCount,
      userExplicitLayout: userExplicitLayoutRef.current,
      isIncremental: isSmallDelta,
    };
    if (currentLayout === 'targetRadial' && targetNodeId) {
      layoutMeta.targetNodeId = targetNodeId;
    }
    adapter.runLayout(preset.options, layoutMeta, () => {
      if (cancelled) return;
      if (showOverlay) setIsLayoutRunning(false);
    });
    return () => {
      cancelled = true;
      if (showOverlay) setIsLayoutRunning(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformResult]);

  // ── Focus node animation ────────────────────────────────────────────
  useEffect(() => {
    if (!focusedNodeId || !adapterRef.current) return;
    const adapter = adapterRef.current;
    adapter.fit([focusedNodeId]);
    adapter.ping(focusedNodeId, 800);

    const pos = adapter.renderedPosition(focusedNodeId);
    if (pos.x !== 0 || pos.y !== 0) {
      setSelectedItemId(focusedNodeId);
      setAnnotationPosition(pos);
    }
  }, [focusedNodeId, focusPingTick]);

  // ── Clear selection when edgeId external clear ──────────────────────
  useEffect(() => {
    if (!edgeId) {
      setAnnotationPosition(null);
      setSelectedItemId(null);
    }
  }, [edgeId]);

  // ── Visible connections (BFS on items dict — no Dijkstra) ───────────
  useEffect(() => {
    if (!showVisible) return;
    const timer = setTimeout(() => {
      const reachable = findReachableFromAdjacency(adjacency, itemsRef.current, contextids);
      if (Object.keys(reachable).length > 0) {
        setVisibleConnections(reachable);
      } else {
        // Fallback: degree > 0
        const degreeMap = {};
        const fallback = {};
        for (const key in itemsRef.current) {
          const item = itemsRef.current[key];
          if (isLink(item)) {
            degreeMap[item.id1] = (degreeMap[item.id1] || 0) + 1;
            degreeMap[item.id2] = (degreeMap[item.id2] || 0) + 1;
          }
        }
        for (const key in itemsRef.current) {
          if (isLink(itemsRef.current[key]) || degreeMap[key] > 0) {
            fallback[key] = itemsRef.current[key];
          }
        }
        setVisibleConnections(fallback);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contextids, showVisible, items, adjacency]);

  // ── Shortest path (single multi-target BFS, S14) ────────────────────
  // One BFS from `shortestPathNodeId` to ALL context ids in one pass,
  // instead of N independent BFS runs (one per context).
  useEffect(() => {
    if (!shortestPathNodeId) return;
    const timer = setTimeout(() => {
      const targets = Object.keys(contextids || {});
      const connectedNodes = bfsPathsFromSource(
        adjacency,
        itemsRef.current,
        shortestPathNodeId,
        targets,
      );
      setPathsConnections(connectedNodes);
    }, 300);
    return () => clearTimeout(timer);
  }, [contextids, shortestPathNodeId, adjacency]);

  // ── Reposition annotation on viewport changes ───────────────────────
  useEffect(() => {
    if (!selectedItemId || !adapterRef.current) return;
    const adapter = adapterRef.current;
    const pos = adapter.renderedPosition(selectedItemId);
    if (pos.x !== 0 || pos.y !== 0) {
      setAnnotationPosition(pos);
    }
  }, [zoom, selectedItemId]);

  // ── Clarity context ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      const item = selectedItemId ? itemsRef.current[selectedItemId] : null;
      if (selectedItemId && item && isNode(item)) {
        const fullNode = nodesDictRef.current[selectedItemId]?.node;
        if (fullNode?.data) {
          setClarityContext(selectedItemId, fullNode.data);
        }
      } else {
        clearClarityContext();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedItemId]);

  useEffect(() => {
    setClarityContextIds(contextids, contexts);
  }, [contextids, contexts]);

  // ── Close dropdown on outside click ─────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Resize handler ──────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setSelectedItemId(null);
      setAnnotationPosition(null);
      adapterRef.current?.getCy()?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Export handlers (useCallback) ───────────────────────────────────
  const exportGraphData = useCallback(() => {
    dispatch(exportGraph());
    setShowExportDropdown(false);
  }, [dispatch]);

  const exportNetworkMapReport = useCallback(() => {
    dispatch(exportNetworkMap());
    setShowExportDropdown(false);
  }, [dispatch]);

  const exportToImage = useCallback(() => {
    if (adapterRef.current) {
      adapterRef.current.exportPng().then((dataUri) => {
        const link = document.createElement('a');
        link.href = dataUri;
        link.download = 'chart.png';
        link.click();
      });
    }
    setShowExportDropdown(false);
  }, []);

  const removeShortestPathId = useCallback(() => {
    dispatch(setShortestPathNodeId(''));
    setPathsConnections(null);
  }, [dispatch]);

  const handleLayoutChange = useCallback((layoutKey) => {
    const preset = LAYOUT_PRESETS[layoutKey];
    if (!preset || !adapterRef.current) return;
    setActiveLayout(layoutKey);
    activeLayoutRef.current = layoutKey;
    userExplicitLayoutRef.current = true;
    // Explicit layout switch: always show overlay (user took an action that
    // genuinely runs a fresh layout, even on small graphs).
    setIsLayoutRunning(true);
    const meta = { userExplicitLayout: true };
    if (layoutKey === 'targetRadial' && targetNodeId) {
      meta.targetNodeId = targetNodeId;
    }
    adapterRef.current.runLayout(preset.options, meta, () => {
      setIsLayoutRunning(false);
      // Release the gate so the next *automatic* topology-change layout
      // can hit the Phase 10 / S13 topology cache. Without this, the flag
      // would stay `true` for the rest of the session after the first
      // explicit pick.
      userExplicitLayoutRef.current = false;
    });
  }, [targetNodeId]);

  // ── Render ──────────────────────────────────────────────────────────
  const hasItems = chartItems && !IsObjectEmpty(chartItems);
  const item = selectedItemId ? itemsRef.current[selectedItemId] : null;

  return (
    <div className={styles.chart_wrapper}>
      {!hasItems && !IsObjectEmpty(contextids) && (
        <div className={styles.no_error}>No Results</div>
      )}

      <div ref={containerRef} style={CONTAINER_STYLE} />

      {/* ── Layout loading overlay ────────────────────────────── */}
      {isLayoutRunning && (
        <div style={LAYOUT_OVERLAY_STYLE}>
          <div className="socket" style={LAYOUT_SPINNER_STYLE}>
            <div className="gel center-gel"><div className="hex-brick h1"></div><div className="hex-brick h2"></div><div className="hex-brick h3"></div></div>
            {['c1 r1','c2 r1','c3 r1','c4 r1','c5 r1','c6 r1','c7 r2','c8 r2','c9 r2','c10 r2','c11 r2','c12 r2','c13 r2','c14 r2','c15 r2','c16 r2','c17 r2','c18 r2'].map(c =>
              <div key={c} className={`gel ${c}`}><div className="hex-brick h1"></div><div className="hex-brick h2"></div><div className="hex-brick h3"></div></div>
            )}
          </div>
          <div style={LAYOUT_TEXT_STYLE}>Optimizing layout…</div>
        </div>
      )}

      {/* ── Annotations ──────────────────────────────────────────── */}
      {selectedItemId != null &&
        item !== undefined &&
        isNode(item) &&
        item?.nodeType === 'filter_session' &&
        annotationPosition != null && (
          <FilterSessionAnnotation
            position={annotationPosition}
            filterCount={item.data?.filterCount}
            resultCount={item.data?.resultCount}
            filters={item.data?.filters}
            isCollapsed={isCollapsed}
            displayedCount={item.data?.displayedCount}
            totalMatchCount={item.data?.totalMatchCount}
            maxLimit={item.data?.maxLimit}
          />
        )}
      {selectedItemId != null &&
        item !== undefined &&
        isNode(item) &&
        !item?.signals &&
        item?.nodeType !== 'filter_session' &&
        annotationPosition != null && (
          <Annotation
            position={annotationPosition}
            itemId={selectedItemId}
            nodesDict={nodesDict}
            isCollapsed={isCollapsed}
          />
        )}
      {selectedItemId != null &&
        item !== undefined &&
        isNode(item) &&
        item?.signals &&
        item?.data &&
        annotationPosition != null && (
          <Annotation
            position={annotationPosition}
            itemId={selectedItemId}
            nodesDict={nodesDict}
            item={item}
            isCollapsed={isCollapsed}
            isSuggested={true}
          />
        )}
      {selectedItemId != null &&
        item !== undefined &&
        isNode(item) &&
        item?.signals &&
        !item?.data &&
        annotationPosition != null && (
          <SuggestedPersonAnnotation
            position={annotationPosition}
            personName={item.value || item.label || 'Unknown'}
            signals={item.signals}
            confidence={item.confidence}
            isCollapsed={isCollapsed}
          />
        )}

      {/* ── No-path message (shortest path) ───────────────────── */}
      {shortestPathNodeId && IsObjectEmpty(chartItems) && (
        <div style={NO_PATH_WRAPPER_STYLE}>
          <p style={NO_PATH_TEXT_STYLE}>
            There is no path to a context node
          </p>
          <div style={GO_BACK_BUTTON_STYLE} onClick={removeShortestPathId}>
            Go Back
          </div>
        </div>
      )}

      {shortestPathNodeId && hasItems && (
        <button
          type="button"
          className={styles.path_reset_button}
          onClick={removeShortestPathId}
          title="Back to full graph"
          aria-label="Back to full graph"
        >
          <MdUndo aria-hidden="true" />
          <span>Full Graph</span>
        </button>
      )}

      {/* ── Layout switcher ──────────────────────────────────── */}
      <LayoutSwitcher onLayoutChange={handleLayoutChange} activeLayout={activeLayout} />

      {/* ── Export dropdown ─────────────────────────────────────── */}
      <div className={styles.export_container} ref={exportDropdownRef}>
        <div className={styles.export_button} onClick={() => setShowExportDropdown(!showExportDropdown)}>
          <AiOutlineExport />
          <MdArrowDropDown className={styles.dropdown_arrow} />
        </div>
        {showExportDropdown && (
          <div className={styles.export_dropdown}>
            <div className={styles.export_option} onClick={exportGraphData}>
              <AiOutlineExport />
              <span>Export CSV</span>
            </div>
            <div className={styles.export_option} onClick={exportToImage}>
              <AiOutlineCamera />
              <span>Export Image</span>
            </div>
            <div
              className={styles.export_option}
              onClick={exportNetworkMapReport}
            >
              <AiOutlineExport />
              <span>Network Map (PDF)</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

CytoscapeChart.displayName = 'CytoscapeChart';

CytoscapeChart.propTypes = {
  items: PropTypes.object,
  isCollapsed: PropTypes.bool,
};

// ── Annotation component ──────────────────────────────────────────────
function Annotation({ position, itemId, nodesDict, isCollapsed, item, isSuggested }) {
  const dispatch = useDispatch();
  const fullNode = nodesDict[itemId]?.node;
  const properties = isSuggested && item?.data ? item.data : fullNode?.data;

  if (!properties) return null;

  const isPerson =
    properties.nodeType === nodeType.Person || properties.nodeType === 'person';
  const { top, left } = getPosition(position, isPerson, isCollapsed);
  const style = {
    top: `${top}px`,
    left: `${left}px`,
    position: 'absolute',
  };

  const moreInformation = () => {
    dispatch(setSelectedVertex(itemId));
  };

  return (
    <div className="annotation" style={style}>
      <div className="annotation-arrow" />
      {isPerson && (
        <PersonCard properties={properties} moreInformation={moreInformation} />
      )}
      {properties.nodeType === nodeType.Organization && (
        <OrganizationCard properties={properties} moreInformation={moreInformation} />
      )}
      {properties.nodeType === nodeType.Fund && (
        <FundCard properties={properties} moreInformation={moreInformation} />
      )}
      {properties.nodeType === nodeType.Education && (
        <EducationCard education={properties} moreInformation={moreInformation} />
      )}
      {properties.nodeType === nodeType.Engagement && (
        <EngagementCard properties={properties} moreInformation={moreInformation} />
      )}
      {properties.nodeType === nodeType.Event && (
        <EventCard properties={properties} moreInformation={moreInformation} />
      )}
    </div>
  );
}

const getPosition = (position, isPerson, isCollapsed) => {
  const { x, y } = position;
  let left = x;
  let top = y;

  const remainingWidth = isCollapsed
    ? window.innerWidth - x
    : window.innerWidth - x - 300;
  const remainingHeight = window.innerHeight - y;
  if (remainingWidth <= 400) {
    left = x - 400;
  } else {
    left = x + 50;
  }
  if (remainingHeight <= 300) {
    top = isPerson ? y - 450 : y - 400;
  } else if (remainingHeight <= 400) {
    top = y - 300;
  } else if (remainingHeight > 500 && y <= 200) {
    top = y;
  } else {
    top = y - 110;
  }
  return { top, left };
};

export default CytoscapeChart;
